import { CommonModule } from '@angular/common';
import { Component, Injector, OnInit, OnDestroy } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import {DEBOUNCE_TIME, extractErrorMessage, goToNextPage} from 'src/app/common/common';
import { MetadataReportViewModel } from 'src/app/model/metadataReports.model';
import { AuthService } from 'src/app/services/auth.service';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import { FormsModule } from '@angular/forms';
import {PaginationClass, PaginationModel} from "../../../../common/pagination";
import {LoaderComponent} from "../../../common/loader/loader.component";
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
  selector: "app-metadata-report-index",
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    NgxPaginationModule,
    FormsModule,
    LoaderComponent,
  ],
  templateUrl: "./metadata-report-index.component.html",
  styleUrl: "./metadata-report-index.component.scss",
})
export class MetadataReportIndexComponent implements OnInit, OnDestroy {
  // Search fields for all columns
  public idSearch: string = '';
  public dataSourceSearch: string = '';
  public reportTypeSearch: string = '';
  public reportSpecifierIdSearch: string = '';
  public labelSearch: string = '';
  public createdAtSearch: string = '';
  public ridCountSearch: string = '';
  public registeredReportsCountSearch: string = '';
  public updatedBySearch: string = '';

  // Debounce search using BehaviorSubject
  private searchParams$ = new BehaviorSubject<any>({
    id: '',
    dataSource: '',
    reportType: '',
    reportSpecifierId: '',
    label: '',
    createdAt: '',
    ridCount: '',
    registeredReportsCount: '',
    updatedBy: ''
  });
  private unsubscribe = new Subject<void>();

  protected readonly paginationId = 'metadataReportsPagination';
  private pagination = new PaginationClass(this.paginationId);
  protected paginationModel: PaginationModel = this.pagination.getPaginationModel();

  public isAdmin: boolean = false;
  public isLoading: boolean = false;
  public error: string | null = null;
  public displayedMetadataReports: MetadataReportViewModel[] = [];
  public router: Router;

  constructor(
    private injector: Injector,
    private authService: AuthService,
    private metadataReportService: ApiService
  ) {
    this.router = injector.get(Router);
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.checkAuthorization();
    this.paginationModel = this.pagination.resetPagination();

    // Setup debounced search that tracks actual parameter changes
    this.searchParams$.pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntil(this.unsubscribe)
    ).subscribe(() => {
      this.loadMetadataReports();
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  buildSearchParams(): CustomHttpParams {
    const params: CustomHttpParams = {
      'depths': JSON.stringify({
        "rids": 1,
        "registered_reports": 1,
      }),
      'limit': this.pagination.itemsPerPage,
      'offset': this.pagination.offset,
    };

    // Add search parameters if they exist
    if (this.idSearch) {
      params['id'] = this.idSearch.trim();
    }
    if (this.dataSourceSearch) {
      params['data_source'] = this.dataSourceSearch.trim();
    }
    if (this.reportTypeSearch) {
      params['report_type'] = this.reportTypeSearch.trim();
    }
    if (this.reportSpecifierIdSearch) {
      params['report_specifier_id'] = this.reportSpecifierIdSearch.trim();
    }
    if (this.labelSearch) {
      params['label'] = this.labelSearch.trim();
    }
    if (this.createdAtSearch) {
      params['created_at'] = this.createdAtSearch.trim();
    }
    if (this.updatedBySearch) {
      params['updated_by'] = this.updatedBySearch.trim();
    }
    // Note: count fields will be client-side filtered since they are computed values

    return params;
  }

  triggerSearch() {
    this.paginationModel = this.pagination.resetPagination();

    // Update the search parameters subject with current values
    this.searchParams$.next({
      id: this.idSearch,
      dataSource: this.dataSourceSearch,
      reportType: this.reportTypeSearch,
      reportSpecifierId: this.reportSpecifierIdSearch,
      label: this.labelSearch,
      createdAt: this.createdAtSearch,
      ridCount: this.ridCountSearch,
      registeredReportsCount: this.registeredReportsCountSearch,
      updatedBy: this.updatedBySearch
    });
  }

  loadMetadataReports() {
    this.isLoading = true;
    this.error = null;

    // Build parameters using the centralized method
    const queryParams = this.buildSearchParams();
    this.metadataReportService.getMetadataReports(queryParams)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: MetadataReportViewModel[]) => {
          this.displayedMetadataReports = response;

          // Apply client-side filtering for computed fields
          this.displayedMetadataReports = this.applyClientSideFilters(this.displayedMetadataReports);

          this.paginationModel = this.pagination.updateTotalItems(this.displayedMetadataReports.length);
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error loading metadata reports:', err);
          this.error = extractErrorMessage(err, "Failed to load metadata reports.");
          this.isLoading = false;
        }
      });
  }

  private applyClientSideFilters(metadataReports: MetadataReportViewModel[]): MetadataReportViewModel[] {
    let filteredReports = metadataReports;

    // Filter by RID count if search term exists
    if (this.ridCountSearch) {
      const searchTerm = this.ridCountSearch.trim();
      filteredReports = filteredReports.filter(report =>
        report.rids && report.rids.length.toString().includes(searchTerm)
      );
    }

    // Filter by registered reports count if search term exists
    if (this.registeredReportsCountSearch) {
      const searchTerm = this.registeredReportsCountSearch.trim();
      filteredReports = filteredReports.filter(report =>
        report.registered_reports && report.registered_reports.length.toString().includes(searchTerm)
      );
    }

    return filteredReports;
  }

  handlePageChange(newPage: number) {
    this.paginationModel = this.pagination.changePage(newPage);
    this.loadMetadataReports();
  }

  nextPage(event: any, url: string, router: Router) {
    return goToNextPage(event, url, router);
  }

  delete(metadataReport: MetadataReportViewModel) {
    if (confirm('Are you sure you want to delete this metadata report?')) {
      this.metadataReportService.deleteMetadataReport(metadataReport.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: () => {
            this.loadMetadataReports(); // Refresh list after deletion
          },
          error: (err) => {
            console.error('Error deleting metadata report:', err);
            this.error = extractErrorMessage(err, "Failed to delete metadata report.");
          }
        });
    }
  }
}
